import * as React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';

import useRoleManager from 'src/hooks/useRoleManager';

import styles from './FeatureUnavailableBanner.module.scss';

const messages = defineMessages({
  badge: {
    id: 'writesPausedBanner.badge',
    defaultMessage: 'Paused',
  },
  paymentRequired: {
    id: 'writesPausedBanner.paymentRequired',
    defaultMessage:
      'Your trial has ended. Uploads and processing are paused until a payment method is added.',
  },
  spendCap: {
    id: 'writesPausedBanner.spendCap',
    defaultMessage:
      'Uploads and processing are paused: this account has reached its spend limit.',
  },
  // `trial_limit_reached` — a free-trial cap (storage, transcoding, delivery,
  // assets); deletes still work, so the account can get back under it.
  trialLimit: {
    id: 'writesPausedBanner.trialLimit',
    defaultMessage:
      'Uploads and changes are paused: this free trial has reached one of its limits.',
  },
  choosePlan: {
    id: 'writesPausedBanner.choosePlan',
    defaultMessage: 'Choose a plan',
  },
  generic: {
    id: 'writesPausedBanner.generic',
    defaultMessage: 'Uploads and processing are paused for this account.',
  },
  askAdmin: {
    id: 'writesPausedBanner.askAdmin',
    defaultMessage: 'Ask an account admin to resume them.',
  },
  addPayment: {
    id: 'writesPausedBanner.addPayment',
    defaultMessage: 'Add a payment method',
  },
  raiseLimit: {
    id: 'writesPausedBanner.raiseLimit',
    defaultMessage: 'Raise the limit',
  },
  viewSubscription: {
    id: 'writesPausedBanner.viewSubscription',
    defaultMessage: 'View subscription',
  },
});

const SUBSCRIPTION_ROUTE = '/admin/subscription';

interface WritesPausedBannerProps {
  /** `reason` from `useWritesPaused()`. */
  reason: string | null;
}

/**
 * Inline notice for a page whose write actions are disabled because the
 * account is refusing writes (see `useWritesPaused`). Same visual family as
 * `PlanUpgradeBanner`.
 *
 * Admins get the one link that resumes the account. Members cannot act on
 * billing — the Subscription page is admin-only — so they are told who can,
 * rather than shown a link that 403s. No prices: this says what stopped and
 * how to restart it, nothing else.
 */
export const WritesPausedBanner: React.FC<WritesPausedBannerProps> = ({ reason }) => {
  const { isAdmin } = useRoleManager();

  const message =
    reason === 'subscription_payment_required'
      ? messages.paymentRequired
      : reason === 'spend_cap_reached'
      ? messages.spendCap
      : reason === 'trial_limit_reached'
      ? messages.trialLimit
      : messages.generic;

  const cta =
    reason === 'subscription_payment_required'
      ? { to: SUBSCRIPTION_ROUTE, label: messages.addPayment }
      : reason === 'spend_cap_reached'
      ? { to: `${SUBSCRIPTION_ROUTE}?purchase=spendLimit`, label: messages.raiseLimit }
      : reason === 'trial_limit_reached'
      ? { to: `${SUBSCRIPTION_ROUTE}/plans`, label: messages.choosePlan }
      : { to: SUBSCRIPTION_ROUTE, label: messages.viewSubscription };

  return (
    <div className={styles.banner} role="status" data-test="writes-paused-banner">
      <span className={styles.badge}>
        <FormattedMessage {...messages.badge} />
      </span>
      <span className={styles.message}>
        <FormattedMessage {...message} />
        {!isAdmin && (
          <>
            {' '}
            <FormattedMessage {...messages.askAdmin} />
          </>
        )}
      </span>
      {isAdmin && (
        <Link to={cta.to}>
          <FormattedMessage {...cta.label} />
        </Link>
      )}
    </div>
  );
};

export default WritesPausedBanner;
